/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState, useEffect } from "react";
import { useDebounce } from "react-use";
import { useGetTagsByValueLazyQuery } from "~/generated/graphql";
import { Autocomplete } from "~/components";
import { DEBOUNCE_TIMEOUT_MS, FETCH_LIMIT, MINIMAL_LENGTH } from "./constants";

type Props = {
  name: string;
  inputValue: string;
  onChange: (value: string) => void;
  debounce?: number;
  fetchLimit?: number;
  minimalLength?: number;
  placeholder?: string;
};

export function InputValue({
  name,
  inputValue,
  onChange,
  debounce = DEBOUNCE_TIMEOUT_MS,
  fetchLimit = FETCH_LIMIT,
  minimalLength = MINIMAL_LENGTH,
  placeholder,
}: Props) {
  const [value, setValue] = useState("");

  const [getTags, { data, loading }] = useGetTagsByValueLazyQuery();

  useEffect(() => {
    setValue(inputValue);
  }, [inputValue]);

  useDebounce(
    () => {
      if (value.length >= minimalLength) {
        void getTags({ variables: { key: name, value, limit: fetchLimit } });
      }
    },
    debounce,
    [value]
  );

  const proposed = data?.searchTagValues?.values ?? [];

  return (
    <Autocomplete
      id="tag-value"
      items={proposed}
      loading={loading}
      value={value}
      placeholder={placeholder}
      onSelect={onChange}
      onChange={(newValue) => {
        setValue(newValue);
        onChange(newValue);
      }}
    />
  );
}
